<template>
  <CCard>
    <CCardHeader>
      <a name="/users/register"></a>
      <h2>ユーザ登録</h2>
    </CCardHeader>
    <CCardBody>
      <p>
        <span class="preika-required-note">
          <span class="preika-required-note__mark" aria-hidden="true">*</span>
          <span>が付いている項目は必須入力項目です</span>
        </span>
      </p>

      <CAlert color="info">
        会社管理者は社員情報と個人連絡先を初期入力できます。電話番号、住所、SlackメンバーIDは、登録後に本人も「個人設定」から変更できます。
      </CAlert>
      <CAlert v-if="roleLoadError" color="danger">{{ roleLoadError }}</CAlert>

      <ValidationObserver ref="obs" v-slot="ObserverProps">
        <CRow v-if="message.length">
          <CCol sm="12">
            <ul class="text-danger">
              <li v-for="error in message" :key="error">{{ error }}</li>
            </ul>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="社員番号" rules="employee|max:20" v-slot="ProviderProps">
              <CInput label="社員番号" v-model="employee" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="氏名" rules="required|name:20" v-slot="ProviderProps">
              <label>氏名<span class="asterisk">*</span></label>
              <CInput v-model="name" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="電話番号" rules="tel" v-slot="ProviderProps">
              <CInput label="電話番号" v-model="telephone" description="ハイフンなしで入力してください" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="8" lg="6">
            <ValidationProvider name="住所" rules="address|max:60" v-slot="ProviderProps">
              <CInput label="住所" v-model="address" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="8" lg="6">
            <ValidationProvider name="E-Mail" rules="required|email|max:254" v-slot="ProviderProps">
              <label>ログインE-Mail<span class="asterisk">*</span></label>
              <CInput v-model="email" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="8" lg="6">
            <ValidationProvider name="E-Mail(確認)" rules="required|email|max:254|check:E-Mail" v-slot="ProviderProps">
              <label>ログインE-Mail(確認)<span class="asterisk">*</span></label>
              <CInput v-model="re_mail" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <label>入社年月日<span class="asterisk">*</span></label>
            <CInput type="date" v-model="hire_date" />
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="パスワード" rules="required|password" v-slot="ProviderProps">
              <label>初期パスワード<span class="asterisk">*</span></label>
              <CInput
                type="password"
                id="pass"
                v-model="password"
                description="英数字混在で8文字以上20文字以下で入力してください。本人は個人設定から変更できます。"
              />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="パスワード(確認)" rules="required|password|check:パスワード" v-slot="ProviderProps">
              <label>初期パスワード(確認)<span class="asterisk">*</span></label>
              <CInput type="password" v-model="re_password" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="8" lg="6">
            <label>所属部署</label>
            <select class="custom-select" v-model="department_id">
              <option value="">未選択</option>
              <option v-for="option in departmentOptions" :value="option.id" :key="'dept-' + option.id">
                {{ option.name }}
              </option>
            </select>
          </CCol>
        </CRow>

        <CRow class="mt-3">
          <CCol sm="12" md="10" lg="8">
            <fieldset class="role-fieldset">
              <legend>初期役割<span class="asterisk">*</span></legend>
              <p class="text-muted small mb-3">
                登録と同時に付与する役割を選択します。複数選択できます。初期状態では「一般社員」が選択されています。
              </p>
              <div v-if="loadingRoles" class="py-3"><CSpinner color="primary" size="sm"/> 役割を読み込んでいます</div>
              <div v-else class="role-grid">
                <label
                  v-for="role in roles"
                  :key="role.id"
                  class="role-option"
                  :class="{ 'role-option--selected': isRoleSelected(role.id) }"
                >
                  <input type="checkbox" :checked="isRoleSelected(role.id)" @change="toggleRole(role.id, $event.target.checked)">
                  <span>
                    <strong>{{ role.name }}</strong>
                    <small v-if="role.description">{{ role.description }}</small>
                  </span>
                </label>
              </div>
              <p v-if="!loadingRoles && !selectedRoleIds.length" class="text-danger small mt-2">
                少なくとも1つの役割を選択してください。
              </p>
            </fieldset>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <div class="d-flex align-items-center">
              <label class="custom-switch mb-0">
                <input type="checkbox" class="custom-switch-input" id="approver-flg" v-model="approver_flg" />
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <label for="approver-flg" class="ml-2 mb-0">申請承認者</label>
            </div>
          </CCol>
          <CCol sm="12" md="6" lg="4">
            <div class="d-flex align-items-center">
              <label class="custom-switch mb-0">
                <input type="checkbox" class="custom-switch-input" id="attendance-exempt-flg" v-model="attendance_exempt_flg" />
                <span class="custom-switch-indicator" aria-hidden="true"></span>
              </label>
              <label for="attendance-exempt-flg" class="ml-2 mb-0">勤怠管理の対象外</label>
            </div>
            <small class="form-text text-muted">
              取締役など、勤務表を出さない方に設定します。月次集計・月次締め・
              全体の勤怠状況に並ばなくなります。ログイン・承認・本人の勤務表入力は
              今までどおりです。
            </small>
          </CCol>
        </CRow>

        <!--
          週稼働日数・稼働曜日・週の所定労働時間。修正画面と同じ部品を使う。
          以前は「4日/3日/2日/1日」が未実装で押せなかった。
        -->
        <WorkPatternInput
          :working-days="working_days"
          :work-weekdays="work_weekdays"
          :daily-work-minutes="daily_work_minutes"
          @update:workingDays="working_days = $event"
          @update:workWeekdays="work_weekdays = $event"
          @update:dailyWorkMinutes="daily_work_minutes = $event"
          @validity="work_pattern_valid = $event"
        />

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="有給日数" rules="holiday|min:-99.9|max:99.9" v-slot="ProviderProps">
              <CInput label="有給日数" v-model="paid" placeholder="10.0" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="代休日数" rules="holiday|min:-99.9|max:99.9" v-slot="ProviderProps">
              <CInput label="代休日数" v-model="comp" placeholder="10.0" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="今期有休消化日数" rules="holiday|min:-99.9|max:99.9" v-slot="ProviderProps">
              <CInput label="今期有休消化日数" v-model="paid_used" placeholder="10.0" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <!--
              有給付与月は「月」なので1〜12から選ぶ。

              以前は有休日数と同じ数値の入力欄で、空のまま送ると
              Number('') が 0 になり、0 で保存されていた。付与SQLは
              1〜12 としか一致しないので、**その人には一度も付与されない**。
              画面にもログにも出ないので、気づくのは本人が有休を取ろうと
              したときになる。
            -->
            <label>有給付与月<span class="asterisk">*</span></label>
            <select class="custom-select" v-model="paid_add">
              <option value="">未選択</option>
              <option v-for="month in grantMonthOptions" :value="month" :key="'grant-' + month">
                {{ month }}月
              </option>
            </select>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="固定内残業" rules="total_time" v-slot="ProviderProps">
              <CInput label="固定内残業" v-model="fixed_over_time" placeholder="00:00" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow>
          <CCol sm="12" md="6" lg="4">
            <ValidationProvider name="SlackメンバーID" rules="max:20" v-slot="ProviderProps">
              <CInput label="SlackメンバーID" v-model="slack_id" description="例：U0123456789" />
              <p class="error">{{ ProviderProps.errors[0] }}</p>
            </ValidationProvider>
          </CCol>
        </CRow>

        <CRow class="mt-3 align-items-center">
          <CCol class="text-start">
            <button class="btn btn-secondary btn-sm" @click="valueCheck">戻る</button>
          </CCol>
          <CCol class="text-center">
            <!--
              **押せない理由を、押せないボタンの隣に出す。**
            
              入社年月日と有給付与月は必須なのに印が無く、しかも未入力でも
              何も出ないまま確認ボタンだけが無効だった。**入れたつもりの人には、
              何が足りないのか分からない。**
            -->
            <CAlert v-if="missingFields.length" color="warning" class="mb-3">
              <b>あと{{ missingFields.length }}つ入力すると、確認へ進めます。</b>
              <ul class="mb-0 mt-2">
                <li v-for="what in missingFields" :key="what">{{ what }}</li>
              </ul>
            </CAlert>

            <button
              type="button"
              @click="openModal"
              class="btn btn-info"
              :disabled="ObserverProps.invalid || loading || loadingRoles || !!roleLoadError || !selectedRoleIds.length || !work_pattern_valid || !paid_add || !hire_date"
            >
              確認
            </button>
            <RegisterModal
              :employee="employee"
              :user="name"
              :tel="telephone"
              :live="address"
              :mail="email"
              :pass="hide"
              :hire_date="hire_date"
              :department_name="department_name"
              :role_names="selectedRoleNames"
              :approver_flg="approver_flg"
              :attendance_exempt_flg="attendance_exempt_flg"
              :working_days="working_days"
              :paid="paid"
              :comp="comp"
              :paid_used="paid_used"
              :paid_add="paid_add"
              :fixed_over_time="fixed_over_time"
              :slack_id="slack_id"
              :authorized="authorized"
              :loading="loading"
              v-show="showModal"
              @register="register"
              @close="closeModal"
            />
          </CCol>
          <CCol></CCol>
        </CRow>
      </ValidationObserver>
    </CCardBody>
  </CCard>
</template>

<script>
import ConstList from './../ConstList'
import RegisterModal from './RegisterModal'
import WorkPatternInput from './WorkPatternInput'
import axios from 'axios'
import { defaultRoleIds, normalizeRoles, roleNames, toggleRoleId } from './roleSupport'

export default {
  name: 'RegisterInput',
  components: { RegisterModal, WorkPatternInput },
  data () {
    return {
      showModal: false,
      employee: '',
      name: '',
      working_days: 5,
      work_weekdays: '',
      daily_work_minutes: '',
      work_pattern_valid: true,
      telephone: '',
      address: '',
      email: '',
      re_mail: '',
      password: '',
      re_password: '',
      hire_date: '',
      approver_flg: false,
      attendance_exempt_flg: false,
      paid: '',
      comp: '',
      paid_used: '',
      paid_add: '',
      fixed_over_time: '',
      slack_id: '',
      department_id: '',
      department_name: '',
      departmentOptions: [],
      roles: [],
      selectedRoleIds: [],
      loadingRoles: true,
      roleLoadError: '',
      hide: '',
      message: [],
      authorized: false,
      list: '',
      loading: false
    }
  },
  computed: {
    /**
     * まだ入っていない必須項目の名前。
     *
     * **押せない条件と、ここは同じものを見る。**片方だけ直すと、
     * 「あと0つです」と出ているのに押せない、という形になる。
     *
     * 画面に出ている名前をそのまま使う。中の変数名を出しても、
     * どこを直せばよいか分からない。
     */
    missingFields () {
      const missing = []
      if (!this.hire_date) { missing.push('入社年月日') }
      if (!this.paid_add) { missing.push('有給付与月') }
      if (!this.selectedRoleIds.length) { missing.push('役割（1つ以上）') }
      if (!this.work_pattern_valid) { missing.push('勤務形態（曜日と1日の時間）') }
      return missing
    },
    /** 有給付与月は1〜12から選ぶ。 */
    grantMonthOptions () {
      return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    },
    selectedRoleNames () {
      return roleNames(this.roles, this.selectedRoleIds)
    }
  },
  created () {
    this.list = ConstList.ROUTE_LIST
  },
  mounted () {
    this.fetchDepartmentOptions()
    this.fetchRoles()
  },
  methods: {
    valueCheck () {
      const hasInput = this.employee || this.name || this.telephone || this.address ||
        this.email || this.re_mail || this.password || this.re_password ||
        this.hire_date || this.department_id || this.approver_flg || this.paid ||
        this.comp || this.paid_used || this.paid_add || this.fixed_over_time || this.slack_id
      if (!hasInput || confirm(ConstList.TRANSITION)) this.$router.push({ name: this.list })
    },
    isRoleSelected (roleId) {
      return this.selectedRoleIds.some(value => String(value) === String(roleId))
    },
    toggleRole (roleId, checked) {
      this.selectedRoleIds = toggleRoleId(this.selectedRoleIds, roleId, checked)
    },
    openModal () {
      this.message = []
      if (!this.selectedRoleIds.length) {
        this.message.push('少なくとも1つの初期役割を選択してください。')
        return
      }
      this.hide = ConstList.ASTERISK.repeat(this.password.length)
      const dept = this.departmentOptions.find(({ id }) => String(id) === String(this.department_id))
      this.department_name = dept ? dept.name : ''
      this.showModal = true
    },
    register () {
      this.loading = true
      this.message = []
      const params = new URLSearchParams()
      params.append(ConstList.EMPLOYEE_ID, this.employee)
      params.append(ConstList.NAME, this.name)
      params.append(ConstList.TELEPHONE, this.telephone)
      params.append(ConstList.ADDRESS, this.address)
      params.append(ConstList.EMAIL, this.email)
      params.append(ConstList.PASSWORD, this.password)
      params.append(ConstList.HIRE_DATE, this.hire_date || '')
      params.append(ConstList.DEPARTMENT_ID, this.department_id === '' ? '' : this.department_id)
      params.append(ConstList.APPROVER_FLG, this.approver_flg)
      params.append(ConstList.ATTENDANCE_EXEMPT_FLG, this.attendance_exempt_flg)
      params.append(ConstList.DELETED_FLG, ConstList.REMAIN)
      params.append(ConstList.PAID_DAYS, Number(this.paid))
      params.append(ConstList.COMP_DAYS, Number(this.comp))
      params.append(ConstList.PAID_DAYS_USED, Number(this.paid_used))
      params.append(ConstList.PAID_DAYS_ADD, Number(this.paid_add))
      params.append(ConstList.WORKING_DAYS, Number(this.working_days))
      // 空文字は「未設定へ戻す」。項目ごと送らないのとは意味が違う。
      params.append(ConstList.WORK_WEEKDAYS, this.work_weekdays)
      params.append(ConstList.DAILY_WORK_MINUTES, this.daily_work_minutes)
      params.append(ConstList.FIXED_OVER_TIME, this.fixed_over_time)
      params.append(ConstList.SLACK_ID, this.slack_id)
      this.selectedRoleIds.forEach(roleId => params.append('roleIds', String(roleId)))

      axios.post(process.env.VUE_APP_API_ORIGIN + '/users/register', params)
        .then(response => {
          if (response.data !== ConstList.SUCCESS) {
            throw new Error('ユーザーを登録できませんでした。入力内容と役割設定を確認してください。')
          }
          this.authorized = true
          alert(ConstList.REGISTER_MESSAGE)
          this.$router.push({ name: this.list })
        })
        .catch(error => {
          this.showModal = false
          this.message = [this.errorMessage(error, 'ユーザーを登録できませんでした。')]
        })
        .finally(() => { this.loading = false })
    },
    closeModal () {
      this.showModal = false
    },
    fetchRoles () {
      this.loadingRoles = true
      this.roleLoadError = ''
      axios.get(process.env.VUE_APP_API_ORIGIN + '/users/roles')
        .then(response => {
          this.roles = normalizeRoles(response.data)
          this.selectedRoleIds = defaultRoleIds(this.roles)
          if (!this.roles.length) throw new Error('選択できる役割がありません。')
        })
        .catch(error => {
          this.roles = []
          this.selectedRoleIds = []
          this.roleLoadError = this.errorMessage(error, '役割を取得できませんでした。')
        })
        .finally(() => { this.loadingRoles = false })
    },
    fetchDepartmentOptions () {
      axios.get(process.env.VUE_APP_API_ORIGIN + '/departments/count')
        .then(countResponse => {
          const totalCount = Number(countResponse.data)
          const allCount = Number.isFinite(totalCount) && totalCount > 0 ? totalCount : 1
          return axios.get(process.env.VUE_APP_API_ORIGIN + '/departments', {
            params: { currentPage: 1, pageSize: allCount }
          })
        })
        .then(response => {
          const list = Array.isArray(response.data) ? response.data : []
          this.departmentOptions = list
            .filter(item => Number(item.deleted_flg) !== ConstList.DELETE)
            .map(item => ({ id: item.id, name: item.name }))
        })
        .catch(error => {
          this.message = [this.errorMessage(error, '部署一覧を取得できませんでした。')]
        })
    },
    errorMessage (error, fallback) {
      const data = error && error.response && error.response.data
      if (data && typeof data === 'object') return data.message || fallback
      if (typeof data === 'string' && data && data !== 'NG') return data
      // axiosが付ける英語のmessage（Network Error など）は画面へ出さない。
      // 画面側が自分で投げたErrorの日本語だけを通す。
      if (error && !error.isAxiosError && error.message) return error.message
      return fallback
    }
  }
}
</script>

<style scoped>
button:disabled { cursor: not-allowed; }
.role-fieldset { border: 1px solid #cbd5e1; border-radius: 12px; padding: 16px; }
.role-fieldset legend { width: auto; padding: 0 8px; margin: 0; font-size: 14px; font-weight: 700; }
.role-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(230px, 1fr)); gap: 10px; }
.role-option { display: flex; gap: 10px; align-items: flex-start; margin: 0; padding: 12px; border: 1px solid #dbe5ec; border-radius: 10px; cursor: pointer; }
.role-option--selected { border-color: #2584c5; background: #eff8ff; }
.role-option input { margin-top: 3px; }
.role-option span { display: grid; gap: 3px; }
.role-option small { color: #64748b; line-height: 1.45; }
</style>
